import { PortalApiError } from "@/lib/portalApi";

/** How many times a genuinely retryable query is retried. */
export const QUERY_RETRY_LIMIT = 3;

/**
 * The app-wide react-query retry policy.
 *
 * Extracted from `main.tsx` so it can be tested — inline in the QueryClient
 * options it was unreachable, and the defect it fixes was invisible in every
 * suite because tests set `retry: false` locally.
 *
 * A 403 is a STATE, not a blip: the caller lacks a grant, and asking three more
 * times cannot change that. Retrying it only delays the honest "you don't have
 * access" message. Measured cost of the previous unconditional `retry: 3`: a
 * denied Space tile fired 4 requests with exponential backoff before its error
 * settled — roughly 7s of spinner before the access banner appeared, and ~48
 * pointless denied requests on a 12-tile Space nobody has grants for.
 *
 * A 504 is a query that already ran past its ceiling (lara-data-plane#2334):
 * reporting stops a statement at 90s and answers 504, and the gateway answers
 * 504 at 120s. Retrying re-runs the same slow query on a source that is
 * already too slow. Measured 2026-10-07: each tile that took the gateway's
 * 504 was re-sent within a second, while its original query was still
 * running, and the re-sends were 429'd and re-sent twice more. So a 504 is
 * shown at once instead (the tile's own message says what to narrow).
 *
 * Everything else keeps the full retry budget, including other 5xx, 429
 * (a busy slot does clear), network failures and client-side throws, none of
 * which carry a status.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (error instanceof PortalApiError && (error.status === 403 || error.status === 504)) {
    return false;
  }
  return failureCount < QUERY_RETRY_LIMIT;
}

/**
 * `shouldRetryQuery` with a smaller budget, for a query that overrides the
 * app-wide limit. A bare numeric `retry: n` replaces the policy outright, so a
 * 403 or 504 on that query was re-sent anyway (gate web #1357).
 */
export function retryAtMost(
  limit: number,
): (failureCount: number, error: unknown) => boolean {
  return (failureCount, error) => failureCount < limit && shouldRetryQuery(failureCount, error);
}

/**
 * The app-wide QueryClient defaults, exported so the WIRING is testable.
 *
 * `shouldRetryQuery` had its own tests, but nothing asserted that anything
 * used it: reverting `main.tsx` to `retry: 3` left the whole suite green. A
 * tested function nobody calls is not a guard, so the object main.tsx passes
 * now lives here where a test can read it.
 */
export const QUERY_CLIENT_DEFAULTS = {
  queries: {
    staleTime: 5 * 60 * 1000,
    // A 403 is a STATE, not a blip — see `shouldRetryQuery`.
    retry: shouldRetryQuery,
    // Window-focus refetch is a footgun for in-progress capture forms: a
    // metadata refetch changes the query's `form` identity, re-runs each
    // form's `fetchData` effect and wipes unsaved input. The portal relies on
    // `staleTime` + explicit `refetch()`, so disable focus refetch globally.
    refetchOnWindowFocus: false,
  },
} as const;
